import { useCallback, useMemo, useState } from 'react';
import { CallView } from './components/CallView';
import { DebugPanel } from './components/DebugPanel';
import { GuestView } from './components/GuestView';
import { InspectionsView } from './components/InspectionsView';
import { LobbyView } from './components/LobbyView';
import { LoginView } from './components/LoginView';
import { SettingsView } from './components/SettingsView';
import { useChecklists } from './config/useChecklists';
import { missingEndpoints } from './config/endpoints';
import { clearSession, getSession, type Session } from './auth/session';
import { formatRemaining, useSessionExpiry } from './auth/useSessionExpiry';
import { usePresence } from './presence/usePresence';
import { StatusPicker } from './components/StatusPicker';
import { readRoute, resolveView } from './routing';
import type { SessionStatus } from './pipeline/Session';

type Tab = 'lobby' | 'call' | 'previous' | 'checklist';

const TABS: { id: Tab; label: string }[] = [
  { id: 'lobby', label: 'Lobby' },
  { id: 'call', label: 'Call' },
  { id: 'previous', label: 'Previous calls' },
  { id: 'checklist', label: 'Checklist' },
];

export function App() {
  const route = useMemo(() => readRoute(), []);
  const [session, setSession] = useState<Session | null>(() => getSession());

  const view = resolveView(route);

  // The customer's route. Never asks for a sign-in, and never loads the
  // checklist machinery.
  if (view === 'guest') {
    return (
      <>
        <GuestView callId={route.callId} />
        {route.debug && <DebugPanel />}
      </>
    );
  }

  // Everything else is staff. Signed out means sign in - not a silent fallback
  // to a guest screen nobody asked for.
  if (!session) {
    return <LoginView onSignedIn={setSession} />;
  }

  return (
    <>
      <InspectorApp
        session={session}
        initialCallId={route.callId}
        onSignOut={() => {
          clearSession();
          setSession(null);
        }}
      />
      {route.debug && <DebugPanel />}
    </>
  );
}

function InspectorApp({
  session,
  initialCallId,
  onSignOut,
}: {
  session: Session;
  initialCallId: string | null;
  onSignOut: () => void;
}) {
  const checklists = useChecklists();
  // Three views behind one tab strip. The call is the default and stays mounted
  // only while it is the one showing - leaving the tab does not leave the call,
  // because CallView holds the LiveSwitch session and unmounting it would drop
  // whoever is on the other end.
  // The lobby, unless a call id came in on the URL - somebody following a link
  // to a specific call means to be on that call, not to browse for one.
  const [tab, setTab] = useState<Tab>(initialCallId ? 'call' : 'lobby');

  /**
   * A room picked in the lobby. Handed to the call view rather than joined
   * here: the moment before joining is when somebody checks their camera and
   * whether the door is shut.
   */
  const [pickedCallId, setPickedCallId] = useState<string | null>(null);
  // The company the room came with, handed to the call so the pricing scope
  // starts with the one answer the conversation will not supply.
  const [pickedOrgId, setPickedOrgId] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionStatus>('idle');

  // The token lasts 24 hours and cannot be refreshed, so it can lapse with the
  // app still open. Watched rather than acted on: signing someone out from here
  // would unmount the call view, which mid-call means hanging up on a customer
  // to show them a login screen.
  const expiry = useSessionExpiry();

  // Lives here rather than in CallView so it survives switching tabs - being
  // marked busy should not depend on which screen is showing.
  const presence = usePresence();

  // There is no backend of ours to health-check. Either the Function URLs were
  // baked into this build or they were not, and that is a deploy-time fact.
  const missing = missingEndpoints();

  const onStatus = useCallback((next: SessionStatus) => setStatus(next), []);

  const listening = status === 'running' || status === 'starting';
  const liveText =
    status === 'running'
      ? 'Listening'
      : status === 'starting'
        ? 'Connecting'
        : status === 'error'
          ? 'Stopped'
          : 'Not listening';

  return (
    <div className="shell">
      <div className="topbar">
        <div className="brand">
          Virtual Inspection<span> / checklist</span>
        </div>
        <div className="spacer" />

        {checklists.selectedConfig && (
          <span className="chip" title="Checklist in use on calls">
            {checklists.selectedConfig.name}
          </span>
        )}

        <div className={`live${listening ? ' on' : ''}${status === 'error' ? ' err' : ''}`}>
          <span className="dot" />
          <span>{liveText}</span>
          <span className="levels">
            <i />
            <i />
            <i />
            <i />
            <i />
          </span>
        </div>

        <StatusPicker presence={presence} />

        <nav className="tabs" aria-label="Sections">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`tab${tab === id ? ' tab--on' : ''}`}
              aria-current={tab === id ? 'page' : undefined}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        <button
          type="button"
          className="ghostbtn"
          title={`Signed in as ${session.user?.email ?? 'staff'}`}
          onClick={onSignOut}
        >
          Sign out
        </button>
      </div>

      {!expiry.valid && (
        <p className="notice notice--bad">
          Your sign-in has expired. Calls started now will not be saved.{' '}
          <button type="button" className="ghostbtn" onClick={onSignOut}>
            Sign in again
          </button>
        </p>
      )}

      {expiry.valid && expiry.expiringSoon && (
        <p className="notice">
          Your sign-in expires in {formatRemaining(expiry.msLeft)}. A call that runs past that
          will not be saved &mdash; worth signing in again first.{' '}
          <button type="button" className="ghostbtn" onClick={onSignOut}>
            Sign in again
          </button>
        </p>
      )}

      {missing.length > 0 && (
        <p className="notice notice--bad">
          This build has no {missing.map((m) => m.endpoint).join(' or ')} endpoint. Set{' '}
          <code>{missing.map((m) => m.envVar).join(' and ')}</code> and rebuild.
        </p>
      )}

      {tab === 'lobby' && (
        <LobbyView
          onJoin={(callId, orgId) => {
            setPickedCallId(callId);
            setPickedOrgId(orgId);
            setTab('call');
          }}
        />
      )}

      {/* The call is hidden rather than unmounted: it owns the LiveSwitch
          session, and unmounting to look at a recording would hang up on
          whoever is on the other end. */}
      <div hidden={tab !== 'call'}>
        <CallView
          config={checklists.selectedConfig}
          configReady={Boolean(checklists.selectedConfig?.fields.length)}
          configLoading={checklists.loading}
          onStatus={onStatus}
          onCallActive={presence.setOnCall}
          initialCallId={initialCallId}
          pickedCallId={pickedCallId}
          pickedOrgId={pickedOrgId}
        />
      </div>
      {tab === 'previous' && <InspectionsView />}
      {tab === 'checklist' && <SettingsView checklists={checklists} />}
    </div>
  );
}
